import { useCallback, useEffect, useMemo, useState } from "react";
import { getCreatives, updateCreativeStatus as apiUpdateCreativeStatus, createAd as apiCreateAd, updateAd as apiUpdateAd } from "../../../api/ads";
import type { Creative, CreativeListParams, CreateAdPayload, UpdateAdPayload } from "../../../api/ads";
import { AdsCampaignStatus } from "../../../api/ads";

export const useCreatives = (initialParams: CreativeListParams = {}) => {
  const defaultParams: CreativeListParams = useMemo(
    () => ({
      pageNum: 1,
      pageSize: 10,
      search: "",
      ...initialParams,
    }),
    [initialParams]
  );

  const [params, setParamsState] = useState<CreativeListParams>(defaultParams);
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [listLoading, setListLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchCreatives = useCallback(
    async (override?: Partial<CreativeListParams>) => {
      try {
        setListLoading(true);
        setError(null);
        const merged: CreativeListParams = { ...params, ...override };
        const response = await getCreatives(merged);
        setCreatives(response.results);
        setTotalCount(response.totalCount);
        setTotalPages(response.totalPages);
      } catch (err: any) {
        setError(err?.message || "Failed to load creatives");
      } finally {
        setListLoading(false);
      }
    },
    [params]
  );

  useEffect(() => {
    fetchCreatives();
  }, [fetchCreatives]);

  const setParams = useCallback((next: Partial<CreativeListParams>) => {
    setParamsState((prev) => ({ ...prev, ...next, pageNum: 1 }));
  }, []);

  const setPageNum = useCallback((pageNum: number) => {
    setParamsState((prev) => ({ ...prev, pageNum }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setParamsState((prev) => ({ ...prev, pageSize, pageNum: 1 }));
  }, []);

  const archiveCreative = useCallback(async (id: string) => {
    setActionLoading(true);
    try {
      setCreatives((prev) => prev.map((c) => (c.id === id ? { ...c, status: AdsCampaignStatus.ARCHIVED } : c)));
    } finally {
      setActionLoading(false);
    }
  }, []);

  const updateCreativeStatus = useCallback(async (id: string, status: AdsCampaignStatus) => {
    const previousCreatives = creatives;
    setCreatives((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    setActionLoading(true);
    try {
      await apiUpdateCreativeStatus(id, status);
    } catch (err: any) {
      setCreatives(previousCreatives);
      setError(err?.message || "Failed to update creative status.");
    } finally {
      setActionLoading(false);
    }
  }, [creatives]);

  const createAd = useCallback(async (payload: CreateAdPayload): Promise<void> => {
    setActionLoading(true);
    try {
      await apiCreateAd(payload);
      await fetchCreatives();
    } finally {
      setActionLoading(false);
    }
  }, [fetchCreatives]);

  const updateAd = useCallback(async (id: string, payload: UpdateAdPayload): Promise<void> => {
    setActionLoading(true);
    try {
      await apiUpdateAd(id, payload);
      await fetchCreatives();
    } finally {
      setActionLoading(false);
    }
  }, [fetchCreatives]);

  return {
    functions: {
      fetchCreatives,
      setParams,
      setPageNum,
      setPageSize,
      archiveCreative,
      updateCreativeStatus,
      createAd,
      updateAd,
    },
    values: {
      creatives,
      params,
      totalCount,
      totalPages,
      error,
    },
    loading: {
      listLoading,
      actionLoading,
    },
  };
};

